// Aides d'affichage de la page Aujourd'hui et de la fiche avant l'appel.
// Toutes les heures passent par shared/time (Intl), jamais par un décalage fixe.

import type { GoogleEvent, ProductKey, Prospect, ProspectStatus, WebinarAttendee } from "@shared/schema";
import type { OutcomeChoice } from "@shared/outcome";
import type { Segment } from "@shared/welya";
import type { Tone } from "@shared/labels";
import type { Objection } from "@shared/objections";
import type { CalendlyAnswer } from "@shared/calendly";
import { formatLocalTime, formatParis } from "@shared/time";
import { CLOSED_STATUSES, addDaysIso, parisDateOf } from "@shared/followup";
import { waLink, type WaTemplate } from "@shared/whatsapp";

export const SEGMENT_LABEL: Record<Segment, string> = {
  A: "A · RDV pris",
  B: "B · Chaud",
  C: "C · Tiède",
  D: "D · Passage éclair",
  E: "E · Pas venu au live",
};

export const SEGMENT_TONE: Record<Segment, Tone> = {
  A: "green",
  B: "coral",
  C: "yellow",
  D: "slate",
  E: "slate",
};

export const OBJECTION_TONE: Record<Objection, Tone> = {
  Prix: "red",
  "Pas encore de clients": "yellow",
  "Formation seule": "sky",
  "Métier / présentiel": "navy",
  Éthique: "violet",
  "Confiance / récurrence": "navy",
  Garantie: "orange",
};

export interface RdvTimes {
  /** Heure dans le fuseau du navigateur : « 9 h 00 ». */
  local: string;
  /** Heure de Paris : « 10 h 00 ». */
  paris: string;
  /** Jour de Paris : « vendredi 23/10 ». */
  parisDay: string;
}

/** Heures d'un RDV, ou null si la date est illisible (formatParis lève une RangeError). */
export function rdvTimes(iso: string | null | undefined): RdvTimes | null {
  if (!iso) return null;
  try {
    const p = formatParis(iso);
    // Espaces insécables : « 10 h 00 » ne se coupe pas en fin de ligne.
    const nb = (t: string) => t.replace(/ /g, "\u00a0");
    return { local: nb(formatLocalTime(iso)), paris: nb(p.time), parisDay: p.day };
  } catch {
    return null;
  }
}

/** Messages du chat, un par ligne (stockés joints par « \n »). */
export function chatLines(attendee: WebinarAttendee | null): string[] {
  return (attendee?.chat ?? "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
}

export function pctSeen(attendee: WebinarAttendee | null): number | null {
  if (!attendee) return null;
  const pct = Math.round(attendee.completion * 100);
  return Math.min(100, Math.max(0, pct));
}

export function formatPoints(score: number): string {
  return Number.isInteger(score)
    ? String(score)
    : score.toLocaleString("fr-FR", { maximumFractionDigits: 1 });
}

/** Réponse à « Clients payants » dans les réponses Calendly, en minuscules (« irrégulier »). */
export function payingClientsAnswer(calendly: CalendlyAnswer[]): string | null {
  const hit = calendly.find((c) => /\bclients?\s+payants?\b/i.test(c.q));
  const a = hit?.a.trim();
  return a ? a.charAt(0).toLowerCase() + a.slice(1) : null;
}

/** « 106 pts · 90 % vu · irrégulier » (parties absentes omises). */
export function rowMeta(
  attendee: WebinarAttendee | null,
  calendly: CalendlyAnswer[],
): string {
  const parts: string[] = [];
  if (attendee) {
    parts.push(`${formatPoints(attendee.score)} pts`);
    const pct = pctSeen(attendee);
    if (pct !== null) parts.push(`${pct} % vu`);
  }
  const paying = payingClientsAnswer(calendly);
  if (paying) parts.push(paying);
  return parts.join(" · ");
}

/** « 2026-10-22 » devient « 22/10 » (sans fuseau : c'est un jour calendaire). */
export function dayMonth(isoDate: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate ?? "");
  return m ? `${m[3]}/${m[2]}` : null;
}

/** Libellés lisibles des gabarits, pour le journal « WhatsApp ouvert : … ». */
export const WA_TEMPLATE_LABEL: Record<WaTemplate, string> = {
  bienvenue: "bienvenue",
  confirmation: "confirmation",
  relance_chaud: "relance chaud",
  premier_message: "premier message",
  absence: "absence",
  relance: "relance",
};

const ABSENCE_GRACE_MS = 30 * 60_000;

/**
 * Gabarit WhatsApp d'une ligne :
 * - « absence » seulement si le RDV a commencé il y a au moins 30 minutes, dans les 7 derniers
 *   jours de Paris (même fenêtre que « À rattraper »), et sans issue ;
 * - jamais « absence » pour un prospect clos (closed_won, closed_lost, rembourse) : il reçoit
 *   « relance » pour tout RDV passé ou sans RDV, et un RDV à venir garde confirmation/bienvenue ;
 * - sinon RDV à venir : confirmation s'il a lieu aujourd'hui (jour de Paris), bienvenue sinon ;
 * - sinon « relance » pour qui a déjà eu l'appel : statut `relance`, RDV passé avec issue
 *   `attended` (celui de la ligne ou `hadAttendedRdv`), ou pas de segment de webinaire (ancien stock) ;
 * - sinon segment B : relance chaud ; tout le reste : premier message.
 * Un RDV ancien sans issue (données d'avant le suivi des issues) suit donc ces deux dernières règles.
 */
export function pickTemplate(
  prospect: Pick<Prospect, "segment" | "status">,
  event: Pick<GoogleEvent, "startTime" | "outcome"> | null | undefined,
  now: Date = new Date(),
  hadAttendedRdv = false,
): WaTemplate {
  if (event) {
    const start = new Date(event.startTime).getTime();
    if (!Number.isNaN(start)) {
      if (start > now.getTime()) {
        return parisDateOf(event.startTime) === parisDateOf(now.toISOString())
          ? "confirmation"
          : "bienvenue";
      }
      const startDay = parisDateOf(event.startTime);
      const today = parisDateOf(now.toISOString());
      const windowStart = today ? addDaysIso(today, -7) : null;
      if (
        !CLOSED_STATUSES.includes(prospect.status) &&
        event.outcome === null &&
        now.getTime() - start >= ABSENCE_GRACE_MS &&
        startDay !== null &&
        windowStart !== null &&
        startDay >= windowStart
      ) {
        return "absence";
      }
    }
  }
  const lineAttended =
    event?.outcome === "attended" && new Date(event.startTime).getTime() <= now.getTime();
  if (
    prospect.status === "relance" ||
    CLOSED_STATUSES.includes(prospect.status) ||
    hadAttendedRdv ||
    lineAttended ||
    prospect.segment === null
  ) {
    return "relance";
  }
  return prospect.segment === "B" ? "relance_chaud" : "premier_message";
}

/** Mode « RDV passé » : le RDV a eu lieu sans issue notée, la ligne propose le message d'absence. */
export function pastRdvMode(
  prospect: Pick<Prospect, "segment" | "status">,
  event: Pick<GoogleEvent, "startTime" | "outcome"> | null | undefined,
  now: Date = new Date(),
): boolean {
  return pickTemplate(prospect, event, now) === "absence";
}

/** Numéro exploitable (même test que le lien WhatsApp) : sert aussi à « Appeler ». */
export function phoneUsable(phone: string | null | undefined): boolean {
  return waLink(phone ?? null, "x") !== null;
}

/** Lien `tel:` du numéro nettoyé ; null sans numéro utilisable. */
export function telHref(phone: string | null | undefined): string | null {
  return phone && phoneUsable(phone) ? `tel:${phone.replace(/\s+/g, "")}` : null;
}

/**
 * Issue à mettre en évidence dans la barre « Issue : » (null : aucune).
 * Un « absent » sur le RDV prime ; sinon le statut de la fiche, avec le produit de la dernière
 * vente pour départager les deux « Closé » (sans vente connue : Power Coaching).
 */
export function selectedIssue(input: {
  status: ProspectStatus;
  eventOutcome: "attended" | "no_show" | null;
  lastSaleProduct: ProductKey | null;
}): OutcomeChoice | null {
  if (input.eventOutcome === "no_show") return "no_show";
  if (input.status === "closed_won") {
    return input.lastSaleProduct === "accelerateur" ? "closed_acc" : "closed_pc";
  }
  if (input.status === "relance") return "relance";
  if (input.status === "closed_lost") return "lost";
  return null;
}
